import { CloseCircleFilled, SearchOutlined } from "@ant-design/icons";
import {
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  notification,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MSG_CHAR_LIMIT, MSG_UNIT_LIMIT, PAGINATION, PhoneNumberRegex } from "src/constants/common";
import { returnEllipsesFileName } from "src/constants/functions";
import { IAddVariable } from "src/constants/types";
import { firstName } from "src/constants/add-variable";
import Colors from "src/themes/colors";
import useWindowDimensions from "src/hooks/useWindowDimensions";
import useFileUpload from "src/hooks/useFileUpload";
import { formatPhoneNumberE164 } from "src/utils/format";
import {
  bulkContactData,
  bulkContactLoading,
} from "src/store/selectors/features/bulkContacts";
import { getMessageLoadingState } from "src/store/selectors/features/message";
import { getCurrentDidId } from "src/store/selectors/features/currentDid";
import { getUser } from "src/store/selectors/features/user";
import RequestAppAction from "src/store/slices/appActions";
import { useBulkResult } from "src/hooks/useBulkResult";
import { useBulkSendProgress } from "src/hooks/useBulkSendProgress";
import { AddBulkScheduleMessageModal } from "../bulk-schedule-mesage-modal";
import ModalButtonContainer from "../modal-button-container";
import TemplateBtn from "../modal-button-container/TemplateBtn";
import ScheduleBtn from "../modal-button-container/ScheduleBtn";
import AttachmentBtn from "../modal-button-container/AttachmentBtn";
import AddVariableBtn from "../modal-button-container/add-variable/AddVariableBtn";
import { replaceContentVariables } from "../modal-button-container/add-variable/utils";
import SendButton from "../button/sendButton";
import BulkResultSummary from "../bulk-result-summary";
import "../../pages/conversations/converstions.scss";

const { Text } = Typography;

interface modalProps {
  openModal: (text?: string) => void;
  closeModal: () => void;
}

interface IBulkMessageModal {
  isModalVisible: boolean;
  setIsModalVisible: (value: boolean) => void;
  userData?: any;
  loading: boolean;
  setLoading: (value: boolean) => void;
  modalRef?: any;
  isEdit?: boolean;
  ref?: any;
}

const BulkMessageModal = ({
  isModalVisible,
  setIsModalVisible,
  userData,
  loading,
  setLoading,
  modalRef,
  isEdit = false,
  ref,
}: IBulkMessageModal) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [form] = useForm();
  const { result, blockError, handleSuccess, handleFailure, reset, setBlockError } = useBulkResult();

  const contacts: any = useSelector(bulkContactData);
  const did = useSelector(getCurrentDidId);
  const user: any = useSelector(getUser);
  const bulkLoading = useSelector(bulkContactLoading);
  const isLoading = useSelector(getMessageLoadingState);

  // --- state ---
  const [currentBatchId, setCurrentBatchId] = useState<number | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [units, setUnits] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>("");
  const [attachments, setAttachments] = useState<any>([]);
  const [characters, setCharacters] = useState<number>(0);
  const [totalCharacters, setTotalCharacters] = useState<String>("");
  const [message, setMessage] = useState<string>("");
  const [variableList, setVariableList] = useState<IAddVariable[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<any>([]);
  const [msgContent, setMsgContent] = useState("");
  const [bulkContacts, setBulkContacts] = useState([]);
  const [newMessage, setNewMessage] = useState(false);
  const [scheduleMsgState, setScheduleMsgState] = useState(false);
  const [contactPage, setContactPage] = useState<number>(1);
  const [contactHasMore, setContactHasMore] = useState<boolean>(true);
  const [value, setValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);

  // --- refs ---
  const textareaRef: any = useRef<any>(null);
  const uploadFileRef: any = useRef<HTMLInputElement | null>(null);
  const scheduleBulkMessageRef = useRef<modalProps>(null);
  // Contact lookup map to resolve names even when the dropdown list changes on search
  const contactNameMap = useRef<Record<string, string>>({});
  const selectRef = useRef<any>(null);
  const prevSearchValue = useRef<string>("");
  // resetProgressRef breaks the circular dep between handleCancel and useBulkSendProgress
  const resetProgressRef = useRef<() => void>(() => {});

  // --- callbacks ---
  const pollStatus = useCallback(
    (batchId: number) => new Promise<any>((resolve) => {
      dispatch(RequestAppAction.getBulkSendStatus({
        id: batchId,
        cbSuccess: (res: any) => resolve(res?.data ?? null),
        cbFailure: () => resolve(null),
      }))
    }),
    [dispatch],
  )

  // Moved before onBatchComplete; only depends on setters already declared above
  const resetSearch = (val: string | null | undefined) => {
    if (val && val?.length > 0) {
      return;
    }

    prevSearchValue.current = "";

    setContactPage(1);
    setContactHasMore(true);
    setIsSearching(false);
  };

  const handleCancel = useCallback(() => {
    reset();
    resetProgressRef.current();
    form.resetFields();
    setCharacters(0);
    setUnits(0);
    setAttachments([]);
    setIsModalVisible(false);
    setTotalCharacters("");
    setMessage("");
    setMsgContent("");
    setSelectedContacts([]);
    setSelectedPhoneNumbers([]);
    contactNameMap.current = {};
    setValue("");
    resetSearch("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, form, setIsModalVisible])

  const onBatchComplete = useCallback((final: any) => {
    dispatch(RequestAppAction.fetchConversations({ id: did }))
    const kept = handleSuccess({ successCount: final.sentCount ?? 0, failedCount: final.failedCount ?? 0, failedNumbers: final.failedNumbers ?? [], failedDetails: final.failedDetails ?? [] })
    if (!kept) {
      handleCancel()
      notification.success({ message: t("notification.success") })
    }
  }, [handleSuccess, dispatch, did, t, handleCancel])

  const { phase, progress, start: startProgress, reset: resetProgress } = useBulkSendProgress({ pollStatus, onComplete: onBatchComplete })

  // Keep the ref in sync so handleCancel always calls the latest resetProgress
  resetProgressRef.current = resetProgress

  const { width } = useWindowDimensions();
  const { setFiles, isFileUploading, success } = useFileUpload();

  useEffect(() => {
    if (!contacts) return;

    const incomingItems: any[] = Array.isArray(contacts?.items) ? contacts.items : [];
    const currentPage: number = contacts?.meta?.page ?? 1;
    const totalPages: number = contacts?.meta?.totalPages ?? 1;

    // Accumulate contact names so tagRender can resolve them even after search resets the dropdown list.
    incomingItems.forEach((c: any) => {
      if (c?.phoneNumber && c?.firstName) {
        contactNameMap.current[c.phoneNumber] = c.firstName + " " + (c?.lastName || "");
      }
    });

    setContactHasMore(currentPage < totalPages);
    setContactPage(currentPage);

    // Build the initial contact list
    setSelectedContacts((prev) => {
      let base: any[];
      if (currentPage === 1) {
        base = incomingItems;
      } else {
        const existingPhones = new Set(prev.filter((c) => !c.temp).map((c) => c.phoneNumber));
        const newItems = incomingItems.filter((c) => !existingPhones.has(c.phoneNumber));
        // Keep any temp entries (raw phone number typed by user) at the top
        const tempEntries = prev.filter((c) => c.temp);
        base = [...tempEntries, ...prev.filter((c) => !c.temp), ...newItems];
      }

      // New number
      if (PhoneNumberRegex.test(value)) {
        const formattedNum = formatPhoneNumberE164(value);
        const alreadyPresent = base.some((c) => c.phoneNumber === formattedNum);
        if (!alreadyPresent) {
          return [{ firstName: undefined, phoneNumber: value, temp: true }, ...base.filter((c) => !c.temp)];
        }
      }

      return base;
    });
  }, [contacts]);

  useImperativeHandle(ref, () => ({
    closeModal: () => {
      form.resetFields();
      setCharacters(0);
      setUnits(0);
      modalRef?.current?.closeModal();
      setMsgContent("");
    },
  }));

  useEffect(() => {
    if (isEdit && textareaRef?.current) {
      const content = form.getFieldValue("content");
      setTotalCharacters(content);
      setMessage(content);
    }
  }, [form, isEdit]);

  useEffect(() => {
    const variables: IAddVariable[] = [];
    variables.push(firstName);
    setVariableList(variables);
  }, []);

  useEffect(() => {
    const charactersLength = totalCharacters.length;
    const totalUnits = Math.floor(charactersLength / 160);
    const chr = charactersLength - (totalUnits || 0) * 160;

    if (charactersLength === 0 && attachments.length === 0) {
      setUnits(0);
    } else if (charactersLength > 0) {
      let val = totalUnits + 1 + attachments?.length * 3;
      setUnits(val);
    } else {
      let val = attachments?.length * 3;
      setUnits(val);
    }

    setCharacters(chr);
  }, [totalCharacters, attachments]);

  const onTextChange = (value: string) => {
    if (blockError) setBlockError(null);
    setMessage(value);
    setTotalCharacters(value.trim());
  }

  const handleTextChange = async (e: any) => {
    onTextChange(e.target.value);
  };

  useEffect(() => {
    const charactersLength = inputValue?.length;
    const totalUnits = Math.floor(charactersLength / 160);

    if (charactersLength === 0) {
      setUnits(0);
    } else {
      setUnits(totalUnits);
    }
  }, [inputValue]);

  const sendMessage = async (values: any) => {
    setLoading(true);

    const phoneNumber: any[] = Array.isArray(values?.phoneNumber)
      ? values?.phoneNumber
      : [values?.phoneNumber];

    const updatedArr = phoneNumber?.map((i: string) =>
      i?.startsWith("+1")
        ? i
        : i?.startsWith("1") && i?.length === 11
        ? `+${i}`
        : `+1${i}`
    );

    try {
      await form.validateFields();
      const data: any = {
        content: replaceContentVariables(values?.content, variableList),
        ...values,
        units: units,
        phoneNumber: updatedArr,
        variableList: variableList,
      };
      if (attachments.length > 0) {
        data["fileLocations"] = attachments;
      }

      if (!did) {
        notification.error({
          message: t("notification.sendMessageFail") + '. ' + t("notification.tryRefresh")
        });
        setLoading(false);
        return;
      }

      dispatch(
        RequestAppAction.postBulkSendAsync({
          id: did,
          data: data,
          cbSuccess: (res: any) => {
            const body = res?.data ?? {}
            const queuedCount = body.queuedCount ?? 0
            if (!queuedCount || !body.batchId) {
              setLoading(false)
              handleFailure(t("notification.bulkSendTotalBlock"))
              return
            }
            setLoading(false)
            setCurrentBatchId(body.batchId)
            startProgress(body.batchId, queuedCount)
          },
          cbFailure: (e: string) => {
            setLoading(false)
            handleFailure(e || t("notification.bulkSendTotalBlock"))
          },
        })
      );
    } catch (e: any) {
      setLoading(false);
      return e;
    }
  };

  // useEffect(() => {
  //   if (did) {
  //     dispatch(
  //       RequestAppAction.fetchBulkContacts({
  //         id: did,
  //         meta: { ...PAGINATION },
  //         cbFailure: (e: string) => {
  //           notification.error({
  //             message: e ?? t("notification.fetchContactFail")
  //           });
  //         },
  //       })
  //     );
  //   }
  // }, [did]);

  const fetchContacts = async (text: string, page: number = 1) => {
    try {
      setLoading(true);

      dispatch(
        RequestAppAction.fetchBulkContacts({
          id: did,
          meta: { ...PAGINATION, page, search: text },
          cbFailure: (e: string) => {
            notification.error({
              message: e ?? t("notification.fetchContactFail")
            });
          },
        })
      );
    } finally {
      setIsSearching(false);
      setLoading(false);
    }
  };

  const handleMenuItemClick = (item: any) => {
    form.setFieldsValue({
      content: item.content,
    });
  };

  const debounceFetch = async (text: string) => {
    fetchContacts(text, 1);
  };

  const handleSearch = async (text: any) => {
    await debounceFetch(text);
  };

  // Fetch more contacts on contact list scroll
  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const isNearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;

    if (isNearBottom && contactHasMore && !bulkLoading) {
      const nextPage = contactPage + 1;
      setContactPage(nextPage);
      fetchContacts(value, nextPage);
    }
  };

  useEffect(() => {
    return () => {
      setInputValue("");
      setUnits(0);
    };
  }, []);

  useEffect(() => {
    if (isFileUploading || !uploadFileRef?.current) {
      return;
    }

    if (success) {
      uploadFileRef.current.value = "";
      setAttachments((pre: any) => [...pre, ...success?.data]);
    } else {
      uploadFileRef.current.value = "";
    }
  }, [isFileUploading]);

  useEffect(() => {
    if (!isModalVisible && did && !contacts) {
      dispatch(
        RequestAppAction.fetchBulkContacts({
          id: did,
          meta: { ...PAGINATION },
          cbFailure: (e: string) => {
            notification.error({
              message: e ?? t("notification.fetchContactFail")
            });
          },
        })
      );
    }

    return () => {
      setUnits(0);
      setCharacters(0);
    };
  }, [isModalVisible]);

  useEffect(() => {
    if (did && isModalVisible) {
      prevSearchValue.current = value;

      if (value === "") {
        return;
      }

      setIsSearching(true);

      // Reset pagination whenever the search value changes
      setContactPage(1);
      setContactHasMore(true);

      if (PhoneNumberRegex.test(value)) {
        setSelectedContacts((prev) => [
          { firstName: undefined, phoneNumber: value, temp: true },
          ...prev.filter((c: any) => !c.temp),
        ]);
      }

      const debounceTimeout = setTimeout(async () => {
        handleSearch(value);
      }, 800);

      return () => clearTimeout(debounceTimeout);
    }
  }, [value]);

  // Fetch more contacts when filtering out selected items leaves < 9 visible
  useEffect(() => {
    if (!contactHasMore || bulkLoading || value?.length > 0) return;

    const currentSelected: string[] = form.getFieldValue("phoneNumber") ?? [];

    const visibleCount = selectedContacts?.length > 0 ? selectedContacts.filter(
      (item: any) => !currentSelected.includes(item?.phoneNumber)
    ).length : 0;

    if (visibleCount < 9 && selectedContacts?.length > 0) {
      fetchContacts(value, contactPage + 1);
    }
  }, [value, contactPage, contactHasMore, bulkLoading, selectedPhoneNumbers]);

  useEffect(() => {
    if (userData) {
      const num = userData?.phoneNumber;
      form.setFieldValue("phoneNumber", num ? [num] : []);
      setNewMessage(true);
    }
  }, [userData]);

  useEffect(() => {
    if (newMessage) {
      setSelectedPhoneNumbers([form.getFieldValue("phoneNumber")]);
      setNewMessage(false);
    }
  }, [newMessage]);

  // Set bulk contacts from all accumulated contacts
  useEffect(() => {
    const rawNumbers = form.getFieldValue("phoneNumber");
    const numbers: string[] = Array.isArray(rawNumbers)
      ? rawNumbers
      : rawNumbers
      ? [rawNumbers]
      : [];
    const contactNames: any = [];

    (Array.isArray(selectedContacts) ? selectedContacts : []).forEach((i: any) => {
      if (numbers.includes(i?.phoneNumber)) {
        contactNames.push({
          phoneNumber: i?.phoneNumber,
          name: i?.firstName
            ? i?.firstName + (i?.lastName || "")
            : i?.phoneNumber,
        });
      }
    });

    // Include any selected numbers not in the contact list (manually entered)
    numbers.forEach((num: string) => {
      if (!contactNames.some((c: any) => c.phoneNumber === num)) {
        contactNames.push({ phoneNumber: num, name: contactNameMap.current[num] || num });
      }
    });

    setBulkContacts(contactNames ?? []);
  }, [selectedContacts, selectedPhoneNumbers]);

  const openScheduleMsgModal = () => {
    setScheduleMsgState(true);

    setMsgContent(form.getFieldValue("content"));
  };

  const handleResendFailed = () => {
    if (!currentBatchId) return;
    setResendLoading(true);
    dispatch(
      RequestAppAction.postBulkResendAsync({
        id: currentBatchId,
        cbSuccess: (res: any) => {
          const body = res?.data ?? {};
          if (body.batchId && body.queuedCount) {
            setCurrentBatchId(body.batchId);
            reset();
            startProgress(body.batchId, body.queuedCount);
          } else {
            notification.info({ message: t("notification.bulkResultNoFailures") });
          }
          setResendLoading(false);
        },
        cbFailure: (e: string) => {
          setResendLoading(false);
          notification.error({ message: e || t("notification.bulkResultNoFailures") });
        },
      })
    );
  };

  useEffect(() => {
    if (scheduleMsgState && selectedPhoneNumbers) {
      // Save data before handleCancel wipes it
      const savedPhoneNumbers = [...selectedPhoneNumbers];
      const savedBulkContacts = [...bulkContacts];
      const savedMsgContent = msgContent;
      const savedAttachments = [...attachments];

      handleCancel();

      setTimeout(() => {
        setSelectedPhoneNumbers(savedPhoneNumbers);
        setBulkContacts(savedBulkContacts);
        setMsgContent(savedMsgContent);
        setAttachments(savedAttachments);

        scheduleBulkMessageRef?.current?.openModal(
          t("heading.scheduleMessage")
        );
        setScheduleMsgState(false);
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleMsgState, handleCancel, t]);

  const handleContactSelectChange = () => {
    if (blockError) setBlockError(null);

    // selectRef.current?.blur();
    const numbers = form.getFieldValue("phoneNumber");

    if (selectedPhoneNumbers !== numbers) {
      setSelectedPhoneNumbers(numbers);
      setValue("");
    }
  };

  const handleContactSelect = () => {
    // selectRef.current?.blur();
    const numbers = form.getFieldValue("phoneNumber");
    if (selectedPhoneNumbers !== numbers) {
      setSelectedPhoneNumbers(numbers);
    }

    //   const contactNames: any = [];
    //   contacts?.items?.map(
    //     (i: {
    //       phoneNumber: string;
    //       firstName: string;
    //       lastName: string;
    //     }) => {
    //       if (numbers?.includes(i?.phoneNumber)) {
    //         contactNames?.push({
    //           phoneNumber: i?.phoneNumber,
    //           name: i?.firstName + (i?.lastName || ""),
    //         });
    //       }
    //     }
    //   );

    // dispatch(
    //   RequestAppAction.fetchBulkContacts({
    //     id: did,
    //     meta: { ...PAGINATION },
    //     cbFailure: (e: string) => {
    //       notification.error({
    //         message: e ?? t("notification.fetchContactFail")
    //       });
    //     },
    //   })
    // );
  };

  return (
    <Modal
      centered
      open={isModalVisible}
      onCancel={handleCancel}
      title={
        <h1 className="font-size-24 font-weight-500">
          <span>{t("modalHeading.newMessage")}</span>
        </h1>
      }
      style={{ borderRadius: 50 }}
      className="add-convertion-modal"
      footer={[]}
    >
      <input
        ref={uploadFileRef}
        onChange={(e) => {
          if (
            attachments?.length < 7
            && e?.target?.files
            && e.target.files.length > 0
          ) {
            setFiles(e.target.files);
          }
        }}
        style={{ display: "none" }}
        type="file"
        multiple
      />
      <Spin spinning={isFileUploading}>
        {phase === "sending" ? (
          <div
            style={{
              minHeight: "26rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              padding: "2rem 0",
            }}
          >
            <Progress
              style={{ width: "100%", marginBottom: "0.5rem" }}
              percent={progress.totalCount ? Math.round((progress.sentCount + progress.failedCount) / progress.totalCount * 100) : 0}
            />
            <p style={{ margin: 0 }}>{t("notification.bulkSendProgress", { sentCount: progress.sentCount, totalCount: progress.totalCount })}</p>
            {progress.failedCount > 0 && (
              <p style={{ margin: "0.25rem 0 0" }}>{t("notification.bulkResultFailedSend", { count: progress.failedCount })}</p>
            )}
          </div>
        ) : result ? (
          <BulkResultSummary
            variant="send"
            successCount={result.successCount}
            failedNumbers={result.failedNumbers}
            failedDetails={result.failedDetails}
            onDone={handleCancel}
            onResend={handleResendFailed}
            resendLoading={resendLoading}
          />
        ) : (
        <Form form={form} onFinish={sendMessage}>
          <Flex vertical gap={10} style={{ padding: "1.3rem 0rem" }}>
            <Space direction="vertical" size={20} className="pb-5">
              <Space direction="vertical" style={{ width: "100%" }}>
                <label
                  className="font-size-16 font-weight-400"
                  style={{ color: Colors.Label }}
                >
                  {t("fields.selectContacts")}
                </label>
                <Form.Item
                  name={"phoneNumber"}
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one contact",
                    },
                  ]}
                >
                  <Select
                    ref={selectRef}
                    mode="multiple"
                    style={{ width: "100%" }}
                    className="custom-select"
                    suffixIcon={
                      bulkLoading || isSearching ? (
                        <Spin size="small" />
                      ) : (
                        <SearchOutlined />
                      )
                    }
                    loading={bulkLoading || isSearching}
                    placeholder="Please select"
                    onPopupScroll={handlePopupScroll}
                    onInputKeyDown={(e) => {
                      if (e.key === "Enter" && (bulkLoading || isSearching)) {
                        if (isSearching && selectedContacts.length > 0) {
                          return;
                        }
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (bulkLoading || isSearching)) {
                        if (isSearching && selectedContacts.length > 0) {
                          return;
                        }
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    onChange={handleContactSelectChange}
                    onSelect={handleContactSelect}
                    onClick={() => {
                      // Refetch if the list is empty or was narrowed by a search.
                      if (selectedContacts?.length === 0 || prevSearchValue?.current !== "") {
                        resetSearch(null);

                        dispatch(
                          RequestAppAction.fetchBulkContacts({
                            id: did,
                            meta: { ...PAGINATION, page: 1 },
                            cbFailure: (e: string) => {
                              notification.error({
                                message: e ?? t("notification.fetchContactFail")
                              });
                            },
                          })
                        );
                      }
                    }}
                    onSearch={(val) => {
                      setValue(val);
                      resetSearch(val);
                    }}
                    filterOption={false}
                    tagRender={(props) => {
                      const { value: tagValue, closable, onClose } = props;
                      const displayName = contactNameMap.current[tagValue] || tagValue;

                      return (
                        <Tag
                          closable={closable}
                          onClose={onClose}
                          style={{
                            marginInlineEnd: 4,
                            maxWidth: 150,
                            display: "inline-flex",
                            alignItems: "center"
                          }}
                        >
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {displayName}
                          </span>
                        </Tag>
                      );
                    }}
                    options={selectedContacts && selectedContacts.length > 0 ? selectedContacts.filter((item: any) => !(selectedPhoneNumbers ?? []).includes(item?.phoneNumber)).map((item: any) => {
                      const displayName = item?.firstName
                        ? item.firstName + " " + (item?.lastName || "")
                        : item?.phoneNumber;
                      return {
                        label: displayName,
                        value: item?.phoneNumber,
                        key: item?.phoneNumber,
                      };
                    }) : []}
                  />
                </Form.Item>
              </Space>

              <div className="d-flex align-items-end pb-5">
                <Space
                  direction="vertical"
                  style={{ width: "100%", position: "relative" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <label
                      className="font-size-16 font-weight-400"
                      style={{ color: Colors.Label }}
                    >
                      {t("fields.message")}
                    </label>
                    <div className="d-flex">
                      <div>
                        {t("sideBar.chars")} {characters}/{MSG_CHAR_LIMIT}
                      </div>
                      <div style={{ marginRight: "0.2rem" }} className="pl-5">
                        {t("sideBar.msgUnits")} {units}/{MSG_UNIT_LIMIT}
                      </div>
                    </div>
                  </div>
                  <Form.Item
                    name={"content"}
                    rules={[
                      // {
                      //   required: true,
                      //   message: "Please enter a message",
                      // },
                      {
                        validator: (_, value) => {
                          if (!!value || attachments?.length > 0) {
                            return Promise.resolve();
                          }
                          return Promise.reject("Please enter a message");
                        },
                      },
                    ]}
                  >
                    <Input.TextArea
                      ref={textareaRef}
                      style={{ resize: "none" }}
                      rows={10}
                      onChange={handleTextChange}
                      placeholder="Type your text here..."
                    />
                  </Form.Item>

                  <ModalButtonContainer>
                    {width > 576 && (
                      <AddVariableBtn
                        disabled={!variableList || variableList.length === 0}
                        variableList={variableList}
                        form={form}
                        textareaRef={textareaRef}
                        message={message}
                        onTextChange={onTextChange}
                        style={{ borderRadius: 50, marginLeft: 10 }}
                      />
                    )}
                    <TemplateBtn
                      handleMenuItemClick={handleMenuItemClick}
                      style={{ borderRadius: 50, marginLeft: 10 }}
                    />
                    <ScheduleBtn
                      form={form}
                      setSelectedPhoneNumbers={setSelectedPhoneNumbers}
                      openScheduleMsgModal={openScheduleMsgModal}
                      style={{ borderRadius: 50, marginLeft: 10 }}
                    />
                    <AttachmentBtn
                      uploadFileRef={uploadFileRef}
                      style={{ borderRadius: 50, marginLeft: 10 }}
                    />
                    <SendButton
                      isDisabled={units > 10 || isLoading || loading}
                      isLoading={units > 10 ? false : (isLoading || loading)}
                      tooltipTitle={units > 10 ? t("message.unitError") : ""}
                    />
                  </ModalButtonContainer>
                </Space>
              </div>
              {blockError && (
                <Text style={{ color: Colors.red }} className="font-size-14">
                  {blockError}
                </Text>
              )}
              {Array.isArray(attachments) && attachments.length > 0 && (
                <Col
                  style={{ display: "flex" }}
                  span={24}
                >
                  {attachments.map((i: string, ind: number) => (
                    <div
                      key={`attachment-${ind}`}
                      style={{ position: "relative", marginRight: "0.5rem" }}
                    >
                      <Tag
                        onClick={() => {
                          window.open(i, "_blank");
                        }}
                        style={{ background: Colors.white, borderRadius: 4 }}
                      >
                        <Text
                          key={ind}
                          className="font-size-14"
                          style={{
                            display: "flex",
                          }}
                        >
                          {returnEllipsesFileName(i)}
                        </Text>
                      </Tag>
                      <CloseCircleFilled
                        onClick={() => {
                          const arr = attachments;
                          setAttachments(arr.filter((item: any) => item !== i));
                        }}
                        style={{
                          color: Colors.red,
                          position: "absolute",
                          top: -10,
                          right: 0,
                          zIndex: 2,
                        }}
                      />
                    </div>
                  ))}
                </Col>
              )}
            </Space>
          </Flex>
        </Form>
        )}
      </Spin>
      <AddBulkScheduleMessageModal
        modalRef={scheduleBulkMessageRef}
        setSelectedPhoneNumber={setSelectedPhoneNumbers}
        phoneNumbers={selectedPhoneNumbers}
        content={msgContent}
        bulkContacts={bulkContacts}
        images={attachments}
      />
    </Modal>
  );
};

export default BulkMessageModal;
